import { getCachedViewSnapshotForSelections } from '@databases/database-state';
import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type PanelManager } from '@databases/panel-types';
import {
  buildCellActions,
  buildColumnActions,
  buildRowActions,
} from '@databases/selection-menu-actions';
import { createEffect } from '@databases/selections';
import {
  type SelectionsApiContract,
  type DatabaseAdapter,
} from '@databases/selections-api-types';
import { type SelectionsActions } from '@databases/selections-api-types';
import { type SelectionsReader } from '@databases/selections-api-types';
import { getService } from '@databases/service-container';
import { type ViewChangeType } from '@databases/utils/database/view/view';

import { contextMenuState } from './state/context-menu-state';
import { createSelectValueMenuContext } from './utils/create-select-value-menu-context';

export interface ContextMenuEffectDeps {
  selectionsApi: SelectionsApiContract;
  databaseAdapter: DatabaseAdapter;
  contentId: string;
  formatMessage: MessageFormatter;
  panelManager: PanelManager;
  selectionsActions: SelectionsActions;
  currentViewId: string;
  highlightsController: HighlightsController;
  selectionsReader: SelectionsReader;
  getIsDBAiFirstCreationEnabled: () => boolean;
}

export const createContextMenuEffect = createEffect(
  ({
    selectionsApi,
    databaseAdapter,
    contentId,
    formatMessage,
    panelManager,
    selectionsActions,
    currentViewId,
    highlightsController,
    selectionsReader,
    getIsDBAiFirstCreationEnabled,
  }: ContextMenuEffectDeps) => {
    let deletePreviewLayer: HighlightsRenderer | null = null;

    return {
      setup() {
        deletePreviewLayer = highlightsController.createLayer();
      },

      dispose() {
        contextMenuState.hide();

        if (deletePreviewLayer) {
          deletePreviewLayer.clear();
          highlightsController.removeLayer(deletePreviewLayer);
          deletePreviewLayer = null;
        }
      },

      deltaTypes: ['selectionReplaced', 'cellEditRequested'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested' || !delta.contextMenuTrigger) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const focusedPosition = delta.focusChange?.next;

        if (!focusedPosition) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const { entryId, fieldId } = selectionsApi.resolvePositionToIds(
          focusedPosition,
          'cell'
        );

        if (!entryId || !fieldId) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const permissions = getService('PermissionsManager').getPermissions();

        const context = createSelectValueMenuContext({
          canEditContent: permissions.canEditContent,
          canEditFields: permissions.canEditFields,
          closeMenu: () => {
            deletePreviewLayer?.clear();
            contextMenuState.hide();
          },
          contentId,
          currentViewId,
          databaseAdapter,
          deletePreviewLayer: deletePreviewLayer ?? undefined,
          entryId,
          fieldId,
          formatMessage,
          isDBAiFirstCreationEnabled: getIsDBAiFirstCreationEnabled(),
          isDeletionDisabled: selectionsReader.isDeleteColumnsDisabled(),
          panelManager,
          resolveEntryIdToRowIndex: (id: string) =>
            databaseAdapter.getVisibleEntryIds().indexOf(id),
          resolveFieldIdToColIndex: (id: string) =>
            databaseAdapter.getVisibleFieldIds().indexOf(id),
          selectionsActions,
          selectionsApi,
        });

        if (!context) {
          deletePreviewLayer?.clear();
          contextMenuState.hide();

          return;
        }

        const view = getCachedViewSnapshotForSelections();
        const actionsBuilder = (setViewChange: (view: ViewChangeType) => void) => ({
          row: buildRowActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            spaceKey: databaseAdapter.getSpaceKey(),
            panelManager,
            isLocked: permissions.isLocked,
          }),
          column: buildColumnActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            setViewChange,
            formatMessage,
          }),
          cell: buildCellActions({
            contentId,
            selectionsApi,
            panelManager,
          }),
        });

        contextMenuState.show(delta.contextMenuTrigger, context, actionsBuilder);
      },
    };
  }
);
